import React from 'react';
import { IdTheftClient } from '../../types';
import { StandardButton } from '../../components/standard';
import { useIdTheftClients } from '../../services/queries/creditManagementQueries';

interface IdTheftDashboardProps {
  onSelectClient: (client: IdTheftClient) => void;
  onBackToDashboard: () => void;
}

const IdTheftDashboard: React.FC<IdTheftDashboardProps> = ({ onSelectClient, onBackToDashboard }) => {
  const { data: clients = [] } = useIdTheftClients();
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white text-lg">🛡️</div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">ID Theft Recovery</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage identity theft cases and fraudulent account removal</p>
            </div>
          </div>
          <StandardButton
            variant="secondary"
            onClick={onBackToDashboard}
          >
            Back to Dashboard
          </StandardButton>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-red-900 dark:text-red-300">Active Cases</h3>
            <p className="text-2xl font-bold text-red-800 dark:text-red-400">{clients.length}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300">Total Fraudulent Accounts</h3>
            <p className="text-2xl font-bold text-blue-800 dark:text-blue-400">4</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-purple-900 dark:text-purple-300">Average Recovery Time</h3>
            <p className="text-2xl font-bold text-purple-800 dark:text-purple-400">45</p>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">Days</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fraudulent Accounts</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Update</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{client.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full font-medium bg-red-100 text-red-800">{client.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-bold text-red-600 dark:text-red-400">{client.fraudulentAccounts}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{client.lastUpdate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <StandardButton
                      variant="ghost"
                      size="sm"
                      onClick={() => onSelectClient(client)}
                    >
                      View Details →
                    </StandardButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IdTheftDashboard;